/* eslint-disable */
import React from "react";

/**
 * Members / staff switch for the deduction and contribution ledgers.
 *
 * The backend keeps both cohorts in one collection, discriminated by
 * `member_type`, and defaults every listing to members — so "MEMBERS" here
 * means "send no member_type at all" rather than an explicit filter. Use
 * cohortQuery() to build the fragment; the listing actions concatenate their
 * fourth argument straight into the URL.
 */
export const COHORT = { MEMBERS: "MEMBERS", STAFF: "STAFF" };

/**
 * Builds the query fragment for a listing call. Pass the live search text and
 * the selected cohort; the result is safe to hand straight to GetAllLoans /
 * GetAllFunds / GetAllMartDeductions as their fourth argument.
 */
export const cohortQuery = (search, cohort) =>
  `${search ? "&search=" + search : ""}${cohort === COHORT.STAFF ? "&member_type=STAFF" : ""
  }`;

/**
 * Shared control classes for the unlabelled filter bars (Loan and Mart
 * ledgers). The `h-10` in all three — these two plus the segmented switch
 * below — is what keeps the search box, cohort switch and per-page select on
 * one line at equal height; change one and change all three.
 *
 * These are literal palette shades on purpose. shadcn's own classes lean on
 * CSS-variable tokens (bg-muted, ring-offset-background, …) that this project
 * has no config for — `theme.extend` is empty — so a copied token would
 * silently render as nothing rather than fail. Tailwind here is 3.1.8, which
 * also predates the -950 shades.
 */
export const FILTER_INPUT_CLASS =
  "flex h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-900 shadow-sm transition-colors placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus-visible:ring-offset-slate-900";

export const FILTER_SELECT_CLASS =
  "flex h-10 shrink-0 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-100 dark:focus-visible:ring-offset-slate-900";

const TABS = [
  { key: COHORT.MEMBERS, text: "Members" },
  { key: COHORT.STAFF, text: "UPNMG Staff" },
];

/**
 * `variant` picks the presentation, and the default keeps every existing call
 * site rendering exactly as before:
 *
 *  - "select" (default): a labelled dropdown, for the Admin bars whose other
 *    controls carry their own stacked labels.
 *  - "segmented": a shadcn-style tab switch that sits inline with an
 *    unlabelled search box and per-page select. `label` becomes the
 *    accessible name instead of visible text, since there is no room for a
 *    stacked label on a single-line bar.
 */
function CohortFilter({ value, onChange, label = "Records", variant = "select" }) {
  if (variant === "segmented") {
    return (
      <div
        role="tablist"
        aria-label={label}
        className="inline-flex h-10 shrink-0 items-center gap-1 rounded-lg  bg-emerald-50 p-1 shadow-sm  dark:bg-gray-800"
      >
        {TABS.map((tab) => {
          const active = value === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(tab.key)}
              className={`inline-flex h-8 items-center justify-center whitespace-nowrap rounded-md px-3 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-800 focus-visible:ring-offset-2 focus-visible:ring-offset-emerald-50 dark:focus-visible:ring-offset-gray-800 ${active
                ? "bg-emerald-100 text-emerald-900 shadow-sm dark:bg-emerald-700 dark:text-emerald-50"
                : "text-emerald-700 hover:text-emerald-900 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
            >
              {tab.text}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="w-full sm:w-44">
      <label
        htmlFor="cohort"
        className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
      >
        {label}
      </label>
      <select
        id="cohort"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-slate-800 dark:text-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
      >
        <option value={COHORT.MEMBERS}>Members</option>
        <option value={COHORT.STAFF}>UPNMG Staff</option>
      </select>
    </div>
  );
}

export default CohortFilter;
